import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, Target, Globe2, TrendingUp, Leaf, Zap, Building2, Database, Users, Award, ChevronRight } from 'lucide-react';
import AnimatedBackground from '../components/AnimatedBackground';

const HomePage = () => {
  const pillars = [
    {
      id: 'sustainability-climate',
      icon: <Leaf className="w-8 h-8" />,
      title: 'Sustainability & Climate',
      description: 'Climate resilience strategies, Net Zero pathways, and sustainable transition frameworks.',
    },
    {
      id: 'tourism-destinations',
      icon: <Building2 className="w-8 h-8" />,
      title: 'Tourism & Destinations',
      description: 'Sustainable tourism strategies, destination management, and carrying capacity planning.',
    },
    {
      id: 'energy-systems',
      icon: <Zap className="w-8 h-8" />,
      title: 'Energy Systems',
      description: 'Decarbonisation pathways, energy efficiency, and ESCO services for sustainable energy.',
    },
    {
      id: 'digital-solutions',
      icon: <Database className="w-8 h-8" />,
      title: 'Digital Solutions',
      description: 'AI tools, IoT monitoring systems, dashboards, and sustainability data platforms.',
    },
  ];

  const services = [
    { id: 'strategy-advisory', name: 'Strategy & Advisory', description: 'Policy-to-implementation frameworks', bgColor: 'bg-gradient-to-br from-red-50 to-red-100', textColor: 'text-red-900', borderColor: 'border-red-200' },
    { id: 'climate-energy', name: 'Climate & Energy', description: 'Net Zero & resilience strategies', bgColor: 'bg-gradient-to-br from-green-50 to-green-100', textColor: 'text-green-900', borderColor: 'border-green-200' },
    { id: 'eu-projects', name: 'EU Projects', description: 'Consortium building & innovation', bgColor: 'bg-gradient-to-br from-blue-50 to-blue-100', textColor: 'text-blue-900', borderColor: 'border-blue-200' },
    { id: 'digital-data', name: 'Digital & Data Systems', description: 'AI, IoT & analytics platforms', bgColor: 'bg-gradient-to-br from-purple-50 to-purple-100', textColor: 'text-purple-900', borderColor: 'border-purple-200' },
    { id: 'esg-compliance', name: 'ESG & Compliance', description: 'Carbon accounting & MRV systems', bgColor: 'bg-gradient-to-br from-orange-50 to-orange-100', textColor: 'text-orange-900', borderColor: 'border-orange-200' },
    { id: 'circular-economy', name: 'Circular Economy', description: 'Sustainable gastronomy & supply chains', bgColor: 'bg-gradient-to-br from-teal-50 to-teal-100', textColor: 'text-teal-900', borderColor: 'border-teal-200' },
  ];

  const partners = [
    { id: 'european-commission', name: 'European Commission' },
    { id: 'local-authorities', name: 'Local Authorities' },
    { id: 'tourism-orgs', name: 'Tourism Organisations' },
    { id: 'private-sector', name: 'Private Sector' },
    { id: 'research-institutions', name: 'Research Institutions' },
    { id: 'ngos-communities', name: 'NGOs & Communities' },
  ];

  const values = [
    {
      id: 'evidence-based',
      icon: <Target className="w-6 h-6" />,
      title: 'Evidence-Based',
      description: 'Data-driven insights and measurable impact',
    },
    {
      id: 'eu-policy',
      icon: <Globe2 className="w-6 h-6" />,
      title: 'EU-Policy Aligned',
      description: 'Expertise in European regulations and frameworks',
    },
    {
      id: 'scalable',
      icon: <TrendingUp className="w-6 h-6" />,
      title: 'Scalable Solutions',
      description: 'Replicability and multi-level governance',
    },
    {
      id: 'stakeholder',
      icon: <Users className="w-6 h-6" />,
      title: 'Stakeholder Engagement',
      description: 'Collaborative and participatory approaches',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-black">
        {/* Custom Animated Background */}
        <div className="absolute inset-0 w-full h-full">
          <AnimatedBackground />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/70 z-10"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-30 max-w-7xl mx-auto px-6 lg:px-8 text-center mt-20">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight animate-fade-in">
            Sustainability, Climate Resilience<br />
            <span className="text-red-600">and Intelligent Systems</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-4xl mx-auto leading-relaxed animate-fade-in-delay">
            For destinations, cities, and organisations across Europe and beyond
          </p>
          <p className="text-lg text-gray-400 mb-12 max-w-3xl mx-auto animate-fade-in-delay-2">
            Strategic advisory · Technical expertise · Digital solutions
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-delay-3">
            <Link to="/services">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white text-lg px-8 py-6 transform hover:scale-105 transition-all duration-300">
                Explore Services
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-black text-lg px-8 py-6 transform hover:scale-105 transition-all duration-300">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
                Enabling Sustainable Transition
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Sustainesis Global Ltd is a Malta-based consultancy and solutions company operating at the intersection of sustainability, climate action, and digital innovation.
              </p>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                We bridge the gap between policy, standards, and implementation—delivering strategic, technical, and digital services to destinations, cities, and organisations across Europe and internationally.
              </p>
              <Link to="/about">
                <Button className="bg-red-600 hover:bg-red-700 text-white">
                  Learn More About Us
                  <ChevronRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1733195296321-b99d129b09cd?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Digital Globe - Global Reach"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-red-600 text-white p-6 rounded-xl shadow-xl">
                <p className="text-sm font-semibold uppercase tracking-wide mb-1">Geographical Scope</p>
                <p className="text-2xl font-bold">Europe + International</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Pillars */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">Core Focus Areas</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Integrated expertise across sustainability, climate, tourism, energy, and digital systems
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {pillars.map((pillar) => (
              <Card key={pillar.id} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 bg-white hover:border-red-600 hover:border-2">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-red-600 rounded-xl flex items-center justify-center text-white mb-6">
                    {pillar.icon}
                  </div>
                  <h3 className="text-xl font-bold text-black mb-3">{pillar.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{pillar.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">Our Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive solutions from strategy to implementation
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {services.map((service) => (
              <div key={service.id} className={`group p-6 rounded-xl border-2 ${service.borderColor} hover:border-red-600 transition-all duration-300 hover:shadow-lg ${service.bgColor}`}>
                <h3 className={`text-lg font-bold ${service.textColor} mb-2 group-hover:text-red-600 transition-colors`}>{service.name}</h3>
                <p className="text-gray-700 text-sm">{service.description}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Link to="/services">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white">
                View All Services
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Who We Work With */}
      <section className="py-24 bg-black text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Who We Work With</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Trusted partners across public, private, and academic sectors
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {partners.map((partner) => (
              <div key={partner.id} className="flex items-center space-x-4 p-6 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-red-600/20 hover:border-red-600 transition-all duration-300">
                <Award className="w-8 h-8 text-red-600 flex-shrink-0" />
                <span className="text-lg font-semibold">{partner.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Sustainesis */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">Why Sustainesis</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Strategic expertise meets technical excellence
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value) => (
              <div key={value.id} className="text-center">
                <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center text-white mb-6 mx-auto">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold text-black mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-red-600 text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Start Your Sustainable Transition?</h2>
          <p className="text-xl mb-10 text-gray-100">
            Let's discuss how we can support your organisation's sustainability goals
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-black hover:bg-gray-100 text-lg px-10 py-6">
              Get in Touch Today
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
